import React, { useState } from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";

export default function ServerLevelingSection({
  channels = [],
  roles = [],
  serverLevelingEnabled,
  setServerLevelingEnabled,
  serverLevelUpChannel,
  setServerLevelUpChannel,
  levelRoles = [],
  setLevelRoles,
  setHasUnsavedChanges,
}) {
  const [newLevel, setNewLevel] = useState(10);
  const [newRoleId, setNewRoleId] = useState("");

  const markDirty = () => {
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  const roleName = (roleId) =>
    roles.find((r) => r.id === roleId)?.name || roleId;

  const sortedRewards = [...(levelRoles || [])].sort(
    (a, b) => Number(a.level) - Number(b.level),
  );

  const addReward = () => {
    const level = Math.min(1000, Math.max(1, Math.floor(Number(newLevel) || 1)));
    if (!newRoleId) return;
    if (
      levelRoles.some(
        (entry) =>
          Number(entry.level) === level && entry.roleId === newRoleId,
      )
    ) {
      return;
    }
    setLevelRoles(
      [...levelRoles, { level, roleId: newRoleId }].sort(
        (a, b) => Number(a.level) - Number(b.level),
      ),
    );
    setNewRoleId("");
    markDirty();
  };

  const removeReward = (level, roleId) => {
    setLevelRoles(
      levelRoles.filter(
        (entry) =>
          !(Number(entry.level) === Number(level) && entry.roleId === roleId),
      ),
    );
    markDirty();
  };

  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Separate from Kami&apos;s global level. Members earn{" "}
        <strong>server rank XP</strong> from chat and voice here, and you can
        grant Discord roles at rank thresholds for permissions. Check progress
        with <code>/slevel</code>. Set a member&apos;s rank with{" "}
        <code>/set-slevel</code>.
      </p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          marginBottom: "20px",
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(serverLevelingEnabled)}
          onChange={(e) => {
            setServerLevelingEnabled(e.target.checked);
            markDirty();
          }}
          style={{ accentColor: "#b85c6e" }}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          SERVER RANKING ENABLED
        </span>
      </label>

      <label className="input-label">SERVER RANK-UP CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={serverLevelUpChannel || ""}
        onChange={(v) => {
          setServerLevelUpChannel(v);
          markDirty();
        }}
        types={[0]}
        emptyLabel="Same channel as the activity (default)"
      />
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.75rem", marginTop: "-8px" }}
      >
        Where server rank-up announcements post. Leave default to announce in
        the channel where they were chatting (or skip for voice-only ups if no
        channel is set).
      </p>

      <label className="input-label" style={{ marginTop: "20px" }}>
        SERVER LEVEL ROLE REWARDS
      </label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.75rem", marginBottom: "12px" }}
      >
        Roles are granted from <strong>server rank</strong> only (not global
        Kami level). Kami needs Manage Roles and must sit above each reward
        role.
      </p>

      {sortedRewards.length > 0 ? (
        <div className="settings-item-list">
          {sortedRewards.map((entry) => (
            <div
              key={`${entry.level}-${entry.roleId}`}
              className="settings-item-row"
            >
              <div className="settings-item-info">
                <strong>Server level {entry.level}</strong>
                <br />
                <span className="settings-item-meta">
                  {roleName(entry.roleId)}
                </span>
              </div>
              <button
                type="button"
                className="manage-btn settings-item-action"
                onClick={() => removeReward(entry.level, entry.roleId)}
              >
                REMOVE
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="help-text" style={{ marginBottom: "15px" }}>
          No server level role rewards yet.
        </p>
      )}

      <label className="input-label">ADD SERVER LEVEL REWARD</label>
      <input
        type="number"
        min="1"
        max="1000"
        className="settings-input"
        value={newLevel}
        onChange={(e) => setNewLevel(e.target.value)}
        placeholder="Level required (e.g. 10)"
      />
      <RolePicker
        roles={roles}
        value={newRoleId}
        onChange={setNewRoleId}
        emptyLabel="Select a role…"
        placeholder="Select a role…"
      />
      <button
        type="button"
        className="manage-btn"
        style={{ marginBottom: "10px" }}
        onClick={addReward}
      >
        ADD REWARD
      </button>
    </SettingsSection>
  );
}
